import React from 'react'
import { observer } from 'mobx-react-lite'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import {
  Dialog,
  Drawer,
  useBudgetsStore,
  useGlobalCopyStore,
  useTransactionStore,
} from '../../../common'
import { type Budget, type DetailedBudget, type Transaction } from '../../../core'

import { TransactionList } from '../../../transactions'

import { SubBudgetZeroState } from '../budgetlist/SubBudgetZeroState'
import { SubBudgetRow } from '../budgetlist/SubBudgetRow'
import { BudgetAmount } from '../shared/BudgetAmount'
import { AddSubBudget } from './AddSubBudget'
import { Header } from './Header'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface SubBudgetListProps {
  budget: DetailedBudget
}

export const SubBudgetList: React.FC<SubBudgetListProps> = observer(({ budget }) => {
  const { category, subBudgets } = budget
  const { budgets: copy, common: commonCopy } = useGlobalCopyStore()
  const { deleteBudget, selectedSubBudget, setAlert, setSelectedSubBudget, updateBudget } =
    useBudgetsStore()
  const { filter, setFilter } = useTransactionStore()

  const [isEditOpen, setIsEditOpen] = React.useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)
  const [amount, setAmount] = React.useState(``)

  React.useEffect(() => {
    if (selectedSubBudget) {
      setAmount(`${selectedSubBudget.amount}`)
      setFilter({
        ...filter,
        custom: (t: Transaction) => t.category_guid === selectedSubBudget.category_guid,
      })
    }
  }, [selectedSubBudget])

  const handleUpdateBudget = async () => {
    if (selectedSubBudget) {
      const result = await updateBudget({
        ...selectedSubBudget,
        amount: Number(amount),
      } as Budget)
      setIsEditOpen(false)
      if (!result) {
        setAlert({
          msg: buildDynamicCopy(
            copy.error_alert_edit_merchant_budget,
            selectedSubBudget.category.name,
          ) as string,
          severity: 'error',
        })
      }
    }
  }

  const handleDeleteBudget = async () => {
    if (selectedSubBudget) {
      const result = await deleteBudget(selectedSubBudget.guid)
      setIsDeleteOpen(false)
      if (result) {
        handleClose()
      } else {
        setAlert({
          msg: buildDynamicCopy(
            copy.error_alert_delete_merchant_budget,
            selectedSubBudget.category.name,
          ) as string,
          severity: 'error',
        })
      }
    }
  }

  const handleClose = () => {
    setFilter({
      ...filter,
      custom: (t: Transaction) =>
        t.category_guid === budget.category_guid ||
        t.top_level_category_guid === budget.category_guid,
    })
    setSelectedSubBudget(undefined)
  }

  return (
    <React.Fragment>
      <List sx={{ bgcolor: 'background.paper' }}>
        {subBudgets.length === 0 ? (
          <SubBudgetZeroState />
        ) : (
          subBudgets.map((subBudget) => (
            <React.Fragment key={subBudget.guid}>
              <SubBudgetRow budget={subBudget} />
              <Divider />
            </React.Fragment>
          ))
        )}
        <AddSubBudget category={category} parentBudget={budget} />
      </List>
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={Boolean(selectedSubBudget)}
        onClose={handleClose}
        onPrimaryAction={() => setIsEditOpen(true)}
        onSecondaryAction={() => setIsDeleteOpen(true)}
        primaryText={copy.details_edit_button}
        secondaryText={copy.details_delete_button}
        title={copy.details_title}
      >
        {selectedSubBudget && (
          <Stack gap={24}>
            <Header budget={selectedSubBudget} />
            <TransactionList height="100%" />
          </Stack>
        )}
      </Drawer>

      {selectedSubBudget && (
        <React.Fragment>
          <Dialog
            copy={{ close_aria: commonCopy.close_aria, title: copy.details_edit_title_subbudget }}
            disablePrimaryButton={amount === '' || Number(amount) < 0 || isNaN(Number(amount))}
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            onPrimaryAction={handleUpdateBudget}
            primaryText={copy.edit_save_button}
            sx={{
              '& .MuiStack-root:first-of-type': {
                pb: 8,
              },
            }}
          >
            <BudgetAmount
              amount={amount}
              categoryName={selectedSubBudget.category.name}
              onAmountChanged={setAmount}
            />
          </Dialog>

          <Dialog
            copy={{ close_aria: commonCopy.close_aria, title: copy.delete_title }}
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onPrimaryAction={handleDeleteBudget}
            primaryColor="error"
            primaryText="Delete"
            secondaryColor="secondary"
          >
            <Text variant="subtitle1">
              {buildDynamicCopy(copy.delete_description, selectedSubBudget.category.name) as string}
            </Text>
          </Dialog>
        </React.Fragment>
      )}
    </React.Fragment>
  )
})
